import { fromJS } from 'immutable';
import moment from 'moment';

import * as ActionType from '../actions/dashboards';
import { dayToString } from '../utils/dateFormat';

import config from '../config';
import { setIn } from '../utils/ChangeSpy';

const widgets = config.main.get('widgets').toJS();
const widgetsOrder = [];

const getWidgets = () => {
  for (const i in widgets) {
    if (Object.prototype.hasOwnProperty.call(widgets, i)) {
      const index = widgetsOrder.length + 1;

      widgets[i].style = {
        left: 0,
        top: 0,
        width: 500,
        height: 200,
        zIndex: 1 + index,
      };

      widgets[i].data = [];

      widgetsOrder.push(i);
    }
  }

  return widgets;
};
const defaultState = fromJS({
  popupDates: {
    to: moment(),
    from: moment().subtract(7, 'days'),
  },
  currentDrag: null,
  positionX: 0,
  positionY: 0,
  searchText: '',
  widgetsOrder,
}).merge({ widgets: fromJS(getWidgets()) });

export default function reducer(state = defaultState, action) {
  switch (action.type) {
    case ActionType.LOADED_WIDGETS:
      return mapResponse(state, action.response);

    case ActionType.MOVE_WIDGET_TO_TOP:
      return moveLastActiveWidgetToTop(state, action.name);

    case ActionType.START_DRAG:
      return moveLastActiveWidgetToTop(
        state.merge({
          currentDrag: action.name,
          positionX: action.x,
          positionY: action.y,
        }),
        action.name,
      );

    case ActionType.DRAG:
      return drag(state, action);

    case ActionType.STOP_DRAG:
      return drag(state, action).mergeDeep({
        currentDrag: null,
      });

    case ActionType.TRIGGER_WIDGET_VISIBILITY:
      return moveLastActiveWidgetToTop(
        state.updateIn(['widgets', action.name], widget => {
          widget = widget.set('visible', !widget.get('visible'));
          widget = widget.setIn(['style', 'left'], 0);
          widget = widget.setIn(['style', 'top'], 0);
          widget = widget.set('portlet', action.name);
          widget = widget.set('loaded', false);
          widget = widget.set('loading', false);
          widget = widget.set('collapse', false);

          return widget;
        }),
        action.name,
      );

    case ActionType.TOGGLE_WIDGET_COLLAPSE:
      return state.updateIn(['widgets', action.name, 'collapse'], collapse => !collapse);

    case ActionType.SORT_WIDGET_DATA:
      return sortWidgetData(state, action);

    case ActionType.LOADING_WIDGET_DATA:
      return state.setIn(['widgets', action.name, 'loading'], true);

    case ActionType.SELECTED_IDS: {
      const path = ['widgets', action.name, 'data', action.order, 'selected'];
      const selected = state.getIn(path);

      return state.setIn(path, !selected);
    }

    case ActionType.SELECTED_ALL_IDS: {
      const data = state.getIn(['widgets', action.name, 'data']).toJS();

      Object.entries(data).forEach(item => {
        state = state.setIn(['widgets', action.name, 'data', item[0], 'selected'], true);
      });

      return state;
    }

    case ActionType.RESET_ALL_IDS: {
      const data = state.getIn(['widgets', action.name, 'data']).toJS();

      Object.entries(data).forEach(item => {
        state = state.setIn(['widgets', action.name, 'data', item[0], 'selected'], false);
      });

      return state;
    }

    case ActionType.LOADED_WIDGET_DATA:
      return state
        .setIn(['widgets', action.name, 'loading'], false)
        .setIn(['widgets', action.name, 'loaded'], true)
        .setIn(['widgets', action.name, 'data'], mapDataResponse(action, state.getIn(['widgets', action.name, 'data'])))
        .setIn(['widgets', action.name, 'meta'], fromJS(action.response.meta));

    case ActionType.CHANGE_SEARCH_TEXT:
      return state.set('searchText', action.text);

    case ActionType.CHANGE_WIDGET_SIZES:
      return moveLastActiveWidgetToTop(
        state.mergeIn(['widgets', action.name, 'style'], {
          height: action.height,
          width: action.width,
        }),
      );

    case ActionType.CHANGE_FIELD:
      return setIn(state, action.field, action.value);

    default:
      return state;
  }
}

function moveLastActiveWidgetToTop(state, widgetName) {
  const widgetsOrder = state
    .get('widgetsOrder')
    .filter(v => v !== widgetName)
    .push(widgetName);
  const widgets = state
    .get('widgets')
    .map((widget, key) => widget.setIn(['style', 'zIndex'], widgetsOrder.indexOf(key)));

  return state.merge({
    widgets,
    widgetsOrder,
  });
}

function drag(state, action) {
  const current = state.get('currentDrag');

  if (current === null) return state;

  return state.mergeDeep({
    currentDrag: current,
    positionX: action.x,
    positionY: action.y,
    widgets: {
      [current]: {
        style: {
          left: state.getIn(['widgets', current, 'style', 'left'], 0) + action.x - state.get('positionX'),
          top: state.getIn(['widgets', current, 'style', 'top'], 0) + action.y - state.get('positionY'),
        },
      },
    },
  });
}

const FIELD_MAP = {
  c: 'columns',
  x: 'left',
  y: 'top',
  w: 'width',
  h: 'height',
};

function mapResponse(state, { dashprefs }) {
  return (dashprefs || []).reduce((result, widget) => {
    const styled = Object.keys(widget).reduce((result, field) => {
      if (field === 'portlet') return setIn(result, ['widgets', widget.portlet, field], widget[field]);
      if (field === 'collapse') return setIn(result, ['widgets', widget.portlet, field], Boolean(widget[field]));

      return setIn(result, ['widgets', widget.portlet, 'style', FIELD_MAP[field]], widget[field]);
    }, result);

    let widgetMoved = moveLastActiveWidgetToTop(styled, widget.portlet);

    widgetMoved = widgetMoved.setIn(['widgets', widget.portlet, 'visible'], true);
    if (widget.c) {
      widgetMoved = widgetMoved.setIn(
        ['widgets', widget.portlet, 'columns'],
        fromJS(filterColumn(widget.c, 'industry', 'execIndustryLabel')),
      );
    }

    return widgetMoved;
  }, state);
}

function mapDataResponse(action, existingData = fromJS([])) {
  const query = action.query || {};
  const isPagination = query.page && Number(query.page) > 0;

  const newData = fromJS(
    action.response.data.map((row, index) => {
      if (row.date) row.date = dayToString(moment(row.date));
      if (row.naDate) row.naDate = dayToString(moment(row.naDate));
      row.order = (isPagination ? existingData.size : 0) + index;
      row.selected = false;

      return row;
    }),
  );

  return isPagination ? existingData.concat(newData) : newData;
}

function sortWidgetData(state, action) {
  const query = {};

  if (action.order && action.order.length) {
    query.sort = action.order.map(o => (o.sort === 'desc' ? `-${o.colId}` : o.colId)).join(',');
  }

  return state.updateIn(['widgets', action.widget], widget =>
    widget.set('query', fromJS(query)).update('columns', columns =>
      action.order.reduce(
        (result, column) => {
          const columnIndex = result.findKey(columnItem => columnItem.get('field') === column.colId);

          if (columnIndex === undefined) return result;

          return result.setIn([columnIndex, 'sort'], column.sort);
        },
        columns.map(column => column.remove('sort')),
      ),
    ),
  );
}

function filterColumn(data, ...rest) {
  return data.filter(d => rest.indexOf(d.field) < 0);
}
